import React, { useEffect, useRef, useState } from 'react';
import { API } from '@/App';
import PollenDotsMap from '@/components/PollenDotsMap';
import ShareMapMenu from '@/components/ShareMapMenu';
import PollenDotsContactFooter from '@/components/PollenDotsContactFooter';
import PollenDotsLegend from '@/components/PollenDotsLegend';
import { StatCard } from '@/pages/PublicJourneyShare';
import { MapPin, Bed, UserRound, Car, Loader2, Sparkles, Users } from 'lucide-react';

/**
 * Public share page for the anonymized "Prints of Connection" atlas.
 * Same editorial design language as the customer journey page. Tuned for
 * investor & vendor audiences — no names, just the network.
 */
const PublicNetworkShare = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/network`);
        if (res.ok) setData(await res.json());
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-teal-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-muted-foreground p-6">
        <BrandBar />
        <p className="mt-4">Could not load map.</p>
      </div>
    );
  }

  const placesAll = data.places || [];
  const cityPlaces = placesAll.filter(p => p.kind !== 'enroute_stop' && p.kind !== 'activity');
  const stopPlaces = placesAll.filter(p => p.kind === 'enroute_stop');
  const activityPins = placesAll.filter(p => p.kind === 'activity');
  const pins = [
    ...cityPlaces.map(p => ({ ...p, type: 'place'  })),
    ...stopPlaces.map(p => ({ ...p, type: 'stop'   })),
    ...activityPins.map(p => ({ ...p, type: 'activity' })),
    ...(data.hotels  || []).map(p => ({ ...p, type: 'hotel'  })),
    ...(data.guides  || []).map(p => ({ ...p, type: 'guide'  })),
    ...(data.drivers || []).map(p => ({ ...p, type: 'driver' })),
  ];
  const totals = data.totals || {};
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-teal-50">
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-10 sm:pt-8 sm:pb-12">
        <BrandBar />

        {/* Hero */}
        <header className="text-center mt-8 mb-6">
          <h1 className="text-2xl sm:text-4xl font-medium tracking-tight leading-tight text-foreground/90">
            <span className="italic font-serif font-normal">Our</span>
            <span> dots of connection</span>
          </h1>
          <p className="text-[10px] tracking-[0.26em] uppercase text-muted-foreground/80 mt-3">
            Made with Love
          </p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto mt-3 leading-relaxed">
            Every traveller we&rsquo;ve carried, every hotel that welcomed us, every guide and driver we&rsquo;ve partnered with &mdash; one growing map of where Pollen Dots has been.
          </p>
        </header>

        {/* MAP — always rendered. Side padding on mobile to allow scroll past. */}
        <div className="relative mx-6 sm:mx-0 rounded-2xl overflow-hidden shadow-[0_10px_40px_-15px_rgba(154,64,20,0.25)] ring-1 ring-amber-200/60 bg-white">
          <PollenDotsMap ref={mapRef} pins={pins} aspect="aspect-[4/5] sm:aspect-[3/4] max-h-[55vh] sm:max-h-[680px]" zoom={4} />
          <div className="absolute right-3 bottom-3 z-[500]">
            <ShareMapMenu
              mapRef={mapRef}
              shareUrl={shareUrl}
              whatsappText="The Pollen Dots atlas — every place we've been, mapped together."
              filename="pollen-dots-network"
            />
          </div>
        </div>
        <PollenDotsLegend />

        {/* Stat ribbon — exactly 6 counters in the requested order */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          <StatCard Icon={Users}     tint="fuchsia" label="Travellers" value={totals.travellers || 0} testid="net-counter-travellers" />
          <StatCard Icon={Sparkles}  tint="orange"  label="Tours"      value={totals.tours || 0}      testid="net-counter-tours" />
          <StatCard Icon={Bed}       tint="sky"     label="Hotels"     value={totals.hotels || 0}     testid="net-counter-hotels" />
          <StatCard Icon={Car}       tint="amber"   label="Drivers"    value={totals.drivers || 0}    testid="net-counter-drivers" />
          <StatCard Icon={UserRound} tint="rose"    label="Guides"     value={totals.guides || 0}     testid="net-counter-guides" />
          <StatCard Icon={MapPin}    tint="teal"    label="Locations"  value={totals.places || 0}     testid="net-counter-locations" />
        </div>

        <PollenDotsContactFooter />
      </div>
    </div>
  );
};

const BrandBar = () => (
  <div className="flex items-center justify-center" data-testid="share-brand-bar">
    <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-7 sm:h-8 w-auto" data-testid="share-brand-logo" />
  </div>
);

export default PublicNetworkShare;
